// bids.ts — Step 3a. Bid-package methods (surfaces the 010.D backend).
// See workers/core-os/src/routes/bids.ts (bid_packages, migration 0035).

import { request } from './request'

export interface BidPackageRow {
  id: string
  project_id: string
  sow_id: string | null
  display_name: string
  scope_category: string | null
  due_at: string | null
  sent_at: string | null
  status: string
  notes: string | null
  awarded_bid_id: string | null
  awarded_at: string | null
  award_email_gmail_draft_id: string | null
  created_at: string
  updated_at: string | null
}

export interface BidPackageListResponse {
  ok: true
  packages: BidPackageRow[]
  count: number
}

export interface DraftEmailResponse {
  ok: true
  draft_id: string
  request_id: string
}

// --- Forward bid->budget money flow (2026-06-01) -----------------------------

export interface LevelingBid {
  id: string
  bid_package_id: string
  contractor_id: string | null
  contractor_display_name: string | null
  total_amount_cents: number | null
  status: string
  leveling_notes: string | null
  notes: string | null
  received_at: string | null
  bid_document_id: string | null
  // Track record (org-level) + price position — the multi-factor inputs.
  avg_rating: number | null
  review_count: number
  would_hire_again_yes: number
  would_hire_again_no: number
  would_hire_again_maybe: number
  price_delta_vs_lowest_cents: number | null
}

export interface LevelingPackage {
  id: string
  display_name: string
  trade: string
  scope_category: string | null
  status: string
  due_at: string | null
  sent_at: string | null
  sow_id: string
  awarded_bid_id: string | null
  awarded_at: string | null
  bid_count: number
  lowest_bid_id: string | null
  lowest_amount_cents: number | null
  recommended_bid_id: string | null
  considers_track_record: boolean
  reviewed_count: number
  bid_comparison_text: string | null
  bid_comparison_generated_at: string | null
  bids: LevelingBid[]
}

export interface ReviewBidInput {
  would_hire_again?: 'yes' | 'no' | 'maybe'
  quality_rating?: number
  schedule_rating?: number
  communication_rating?: number
  cleanup_rating?: number
  notes?: string
}

export interface LevelingResponse {
  ok: true
  packages: LevelingPackage[]
  overlaps: { trade: string; packages: { id: string; name: string }[] }[]
  gaps: { trade: string; scope_item_count: number }[]
  totals: {
    package_count: number
    awarded_count: number
    bids_in: number
    total_awarded_cents: number
  }
}

export interface CreatePackageInput {
  trade?: string
  sow_id?: string
  display_name: string
  scope_category?: string
  due_at?: string
  notes?: string
}

export interface AddBidInput {
  contractor_id: string
  total_amount_cents: number
  leveling_notes?: string
  notes?: string
}

export interface AwardResponse {
  ok: true
  awarded_bid_id: string
  award_id: string
  commitment_id: string
  scs_line_id: string
  budgeted_cents: number
  project_contractor_added: boolean
  losing_bids_rejected: number
  request_id: string
}

export const bidsApi = {
  listPackages: (projectId: string) =>
    request<BidPackageListResponse>(
      `/api/projects/${encodeURIComponent(projectId)}/bid-packages`,
    ),
  getPackage: (projectId: string, bidPackageId: string) =>
    request<Record<string, unknown>>(
      `/api/projects/${encodeURIComponent(projectId)}/bid-packages/${encodeURIComponent(
        bidPackageId,
      )}`,
    ),
  // Patch 010.D.3 — draft the bid-award onboarding email for an awarded
  // package (010.D.2 part 2).
  awardEmail: (projectId: string, bidPackageId: string) =>
    request<DraftEmailResponse>(
      `/api/projects/${encodeURIComponent(projectId)}/bid-packages/${encodeURIComponent(
        bidPackageId,
      )}/award-email`,
      { method: 'POST', body: '{}' },
    ),
  // Patch 010.D.3 — draft the insurance-contact COI forward (010.D.2
  // part 4). coi-records routes are org-level (not project-scoped).
  draftInsurance: (coiId: string) =>
    request<DraftEmailResponse>(
      `/api/coi-records/${encodeURIComponent(coiId)}/draft-insurance`,
      { method: 'POST', body: '{}' },
    ),

  // Forward bid->budget money flow (2026-06-01).
  leveling: (projectId: string) =>
    request<LevelingResponse>(
      `/api/projects/${encodeURIComponent(projectId)}/bid-leveling`,
    ),
  createPackage: (projectId: string, body: CreatePackageInput) =>
    request<{ ok: true; id: string; sow_id: string; status: string; request_id: string }>(
      `/api/projects/${encodeURIComponent(projectId)}/bid-packages`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  addBid: (projectId: string, bidPackageId: string, body: AddBidInput) =>
    request<{ ok: true; id: string; bid_package_id: string; status: string; request_id: string }>(
      `/api/projects/${encodeURIComponent(projectId)}/bid-packages/${encodeURIComponent(
        bidPackageId,
      )}/bids`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  award: (projectId: string, bidPackageId: string, bidId: string) =>
    request<AwardResponse>(
      `/api/projects/${encodeURIComponent(projectId)}/bid-packages/${encodeURIComponent(
        bidPackageId,
      )}/award`,
      { method: 'POST', body: JSON.stringify({ bid_id: bidId, confirm: true }) },
    ),
  // Cancel a bid package (e.g. an overlap duplicate). Marks it cancelled +
  // archives it off the active list; logged bids are kept. Reuses the
  // existing PATCH .../bid-packages/:id endpoint.
  cancelPackage: (projectId: string, bidPackageId: string) =>
    request<{ ok: true; id: string; updated: string[] }>(
      `/api/projects/${encodeURIComponent(projectId)}/bid-packages/${encodeURIComponent(
        bidPackageId,
      )}`,
      { method: 'PATCH', body: JSON.stringify({ status: 'cancelled', archived: true }) },
    ),
  // Compare bids (AI): hand the package's bids (+ quote PDFs) to Claude and
  // get back a line-item, scope/spec-normalized comparison + net read.
  compareBids: (projectId: string, bidPackageId: string) =>
    request<{
      ok: true
      comparison_text: string
      bids_compared: number
      pdfs_attached: number
      cost_usd: number | null
      request_id: string
    }>(
      `/api/projects/${encodeURIComponent(projectId)}/bid-packages/${encodeURIComponent(
        bidPackageId,
      )}/compare-bids`,
      { method: 'POST', body: '{}' },
    ),
  // Attach a quote document to a bid (after uploading via documentsApi.upload).
  setBidDocument: (projectId: string, bidPackageId: string, bidId: string, documentId: string) =>
    request<{ ok: true; id: string; updated: string[] }>(
      `/api/projects/${encodeURIComponent(projectId)}/bid-packages/${encodeURIComponent(
        bidPackageId,
      )}/bids/${encodeURIComponent(bidId)}`,
      { method: 'PATCH', body: JSON.stringify({ bid_document_id: documentId }) },
    ),
  // Review a losing bidder + pass on them: saves an org-level review and
  // archives the bid off this project.
  reviewBid: (
    projectId: string,
    bidPackageId: string,
    bidId: string,
    body: ReviewBidInput,
  ) =>
    request<{ ok: true; bid_id: string; rating_id: string | null; archived: true; request_id: string }>(
      `/api/projects/${encodeURIComponent(projectId)}/bid-packages/${encodeURIComponent(
        bidPackageId,
      )}/bids/${encodeURIComponent(bidId)}/review`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
}
